import {
  CustomMappingAssociatedValueType,
  CustomMappingOption,
  CustomMappingType,
  ICustomMapping,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { CustomMapping } from '../custom-mapping';
import {
  ICustomFormDataResolverConfig,
  FormSchemaPropertyType,
  IFormSchema,
} from '@principle-theorem/principle-core/interfaces';
import { IBaseAllergy } from '../interfaces';
import {
  asyncForEach,
  Firestore,
  IBlobFilenamePair,
  isArray,
  isObject,
  multiSwitchMap,
  snapshot,
  WithRef,
  XSLXImporterExporter,
} from '@principle-theorem/shared';
import { cloneDeep, first, get, set, sortBy } from 'lodash';
import { BaseSourceEntity } from '../source/base-source-entity';
import {
  Brand,
  CustomFormConfiguration,
  BASIC_MEDICAL_HISTORY_FORM_SCHEMA,
} from '@principle-theorem/principle-core';
import { from, Observable, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { recursiveReplace } from '@principle-theorem/editor';
import { TranslationMapHandler } from '../translation-map';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import {
  AllergyToMedicalHistoryToXSLX,
  IMedicalHistoryOption,
} from './allergy-to-medical-history-to-xlsx';
import { XSLXToAllergyToMedicalHistory } from './xlsx-to-allergy-to-medical-history';
import { PracticeMigration } from '../practice-migrations';

export const ALLERGY_CUSTOM_MAPPING_TYPE = 'medicalAllergyMapping';

export interface ICustomFormDataResolverConfigExtended
  extends ICustomFormDataResolverConfig {
  type: FormSchemaPropertyType;
  associatedValues?: string[];
}

export const ALLERGY_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Allergy Mapping',
    description: `Used to map allergies from the source PMS to Principle medical history.`,
    type: ALLERGY_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
});

export abstract class BaseAllergyToMedicalHistoryMappingHandler<
  Allergy extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<Allergy>,
> extends BaseCustomMappingHandler<object, string> {
  abstract sourceEntity: SourceEntity;
  customMapping = ALLERGY_MAPPING;

  abstract translateFn: (record: Allergy['data']) => IBaseAllergy;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map((record) => {
        const allergy = this.translateFn(record);
        return {
          label: allergy.description,
          value: allergy.id.toString(),
        };
      });

    return sortBy(options, 'label');
  }

  async getSelectionListOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return snapshot(
      Brand.medicalHistoryFormConfig$(migration.configuration.brand).pipe(
        switchMap((config) =>
          config ? CustomFormConfiguration.getContent$(config) : of(undefined)
        ),
        map((content) => {
          const schema =
            content?.jsonSchemaForm.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;
          return [
            {
              value: '',
              description: 'Omit',
              hasAssociatedValue: false,
            },
            ...getValidMedicalHistoryFormOptions(schema).map((option) => ({
              value: option.path,
              description: option.mapTitle ?? '',
              hasAssociatedValue: option.associatedValues ? true : false,
              associatedValueType: CustomMappingAssociatedValueType.String,
              associatedValueDescription: 'Select an option',
            })),
          ];
        })
      )
    );
  }

  getAssociatedValueOptions$(
    migration: IPracticeMigration,
    destinationValue: { value: string }
  ): Observable<{ name: string }[]> {
    return Brand.medicalHistoryFormConfig$(migration.configuration.brand).pipe(
      switchMap((config) =>
        config ? CustomFormConfiguration.getContent$(config) : of(undefined)
      ),
      map((content) => {
        const schema =
          content?.jsonSchemaForm.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;
        const selectedOption = getValidMedicalHistoryFormOptions(schema).find(
          (option) => option.path === destinationValue.value
        );
        return (selectedOption?.associatedValues ?? []).map((name) => ({
          name,
        }));
      })
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, allergies, translators } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, allergies, translators);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, allergies, translators } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, allergies, translators);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToAllergyToMedicalHistory()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const medicalHistoryOptions =
      await this._getMedicalHistoryOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const mapTo = item.mapTo;
      const id = item.sourceId;
      const label = item.sourceName;
      const value = sourceOptions.find((option) => option.value === id)?.value;

      if (!value || !mapTo) {
        return;
      }

      const matchingOption = medicalHistoryOptions.find(
        (option) => option.label === mapTo
      );
      if (!matchingOption) {
        // eslint-disable-next-line no-console
        console.error(
          `No matching medical history option found for "${mapTo}"`
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          associatedValue: matchingOption.associatedValue,
          destinationValue: matchingOption.destinationValue,
          sourceIdentifier: value,
          sourceLabel: label,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    allergies: ICustomMappingSourceOption[];
    translators: AllergyToMedicalHistoryToXSLX;
  }> {
    const fileName = this.getFileName();
    const allergies = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);

    const medicalHistoryOptions =
      await this._getMedicalHistoryOptions(migration);
    const translators = new AllergyToMedicalHistoryToXSLX(
      medicalHistoryOptions,
      await this.getRecords(translationMap)
    );
    return { fileName, allergies, translators };
  }

  private async _getMedicalHistoryOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IMedicalHistoryOption[]> {
    return snapshot(
      from(this.getSelectionListOptions(migration)).pipe(
        multiSwitchMap((option) => {
          if (!option.hasAssociatedValue) {
            return of([
              {
                destinationValue: { value: option.value },
                label: option.description,
              },
            ]);
          }

          return this.getAssociatedValueOptions$(migration, option).pipe(
            map((associatedValues) => {
              return associatedValues.map((associatedValue) => ({
                destinationValue: { value: option.value },
                associatedValue: associatedValue.name,
                label: `${option.description} - ${associatedValue.name}`,
              }));
            })
          );
        }),
        map((options) => options.flat())
      )
    );
  }
}

export function getValidMedicalHistoryFormOptions(
  schema: IFormSchema
): ICustomFormDataResolverConfigExtended[] {
  const options: ICustomFormDataResolverConfigExtended[] = [];
  recursiveReplace(cloneDeep(schema), (option, _parents, key) => {
    if (isObject(option.properties) && 'properties' in option) {
      return option.properties;
    }

    if (isObject(option) && 'type' in option && 'title' in option) {
      const type = option.type as FormSchemaPropertyType;
      const associatedValues =
        'enum' in option && isArray(option.enum)
          ? option.enum.map((value) => String(value))
          : undefined;
      if (
        [
          FormSchemaPropertyType.Boolean,
          FormSchemaPropertyType.String,
        ].includes(type)
      ) {
        options.push({
          path: key ?? '',
          mapTitle: String(option.title),
          type,
          associatedValues,
        });
      }
    }

    return option;
  });

  return sortBy(options, 'mapTitle');
}

export function setCustomFormValue(
  formOption: ICustomFormDataResolverConfigExtended,
  formData: object,
  booleanValue: boolean = false,
  associatedValue: string | undefined = first(formOption.associatedValues),
  stringValue: string = ''
): object {
  const updatedData = cloneDeep(formData);
  if (formOption.associatedValues) {
    set(updatedData, formOption.path, associatedValue);
    return updatedData;
  }

  if (formOption.type === FormSchemaPropertyType.Boolean) {
    set(updatedData, formOption.path, booleanValue);
    return updatedData;
  }

  set(
    updatedData,
    formOption.path,
    `${get(updatedData, formOption.path, '')} ${stringValue}`.trim()
  );
  return updatedData;
}
